/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { isOneWayBooking, formatVehicleEventTime } from "utils/vehicleBooking";
import Calendar from "components/Calendar/Calendar.js";
// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Fullcalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import Datetime from "react-datetime";

// core components
import Grid from "components/Grid/GridContainer";
import Col from "components/Grid/GridItem";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import TSCheckBox from "components/Forms/TSCheckBox";
import TSSelect2 from "components/Forms/TSSelect2";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";

import "react-datetime/css/react-datetime.css";
import "./custom-style.css";
import { VehicleAdminGuard } from "./vehicleAuth";

const styles = {
  cardCategoryWhite: {
    color: "rgba(255,255,255,.62)",
    margin: "0",
    fontSize: "14px",
    marginTop: "0",
    marginBottom: "0"
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "bold",
    fontFamily: '"Sarabun", sans-serif',
    marginBottom: "3px",
    textDecoration: "none"
  }
};

const PALETTE = [
  "#2563eb", // Blue
  "#059669", // Emerald
  "#d97706", // Amber
  "#7c3aed", // Purple
  "#db2777", // Pink
  "#0891b2", // Cyan
  "#ea580c", // Orange
  "#4f46e5", // Indigo
  "#16a34a", // Green
  "#be123c", // Rose
  "#0d9488", // Teal
  "#475569" // Slate
];

const getVehicleColor = key => {
  const num = Math.abs(Number(key) || 0);
  return PALETTE[num % PALETTE.length];
};

const getDriverColor = str => {
  if (!str) return "#64748b";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
};

const useStyles = makeStyles(styles);

const Home = props => {
  const moment = require("moment");
  require("moment/locale/th");

  // 1. Data state
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [rawTurns, setRawTurns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // 2. View & Filter state
  // perspective: "vehicle" (มุมมองรถ) or "driver" (มุมมองคนขับ)
  const [perspective, setPerspective] = useState("vehicle");
  const [selectedVehicleKey, setSelectedVehicleKey] = useState(0); // 0 = all
  const [selectedDriverUser, setSelectedDriverUser] = useState(""); // "" = all
  const [isShowCalendar, setIsShowCalendar] = useState(true);
  const [defaultDate, setDefaultDate] = useState(moment().format("YYYY-MM-DD"));

  // 3. Detail Dialog state
  const [detailModal, setDetailModal] = useState(false);
  const [selectedTurn, setSelectedTurn] = useState(null);

  const classes = useStyles();

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      const [resVehicles, resDrivers, resTurns] = await Promise.all([
        axios.get("/api/v1/vehicle/vehicle"),
        axios.get("/api/v1/vehicle/drivers").catch(() => ({ data: { data: [] } })),
        axios.get("/api/v1/vehiclebooking/assigment/")
      ]);

      // Process vehicles
      const vData = resVehicles.data?.data || [];
      const vList = vData.map(v => ({
        value: v.vehicleKey,
        label: `${v.name} ${v.licensePlate ? `(${v.licensePlate})` : ""}`,
        name: v.name,
        licensePlate: v.licensePlate,
        vehicleKey: v.vehicleKey
      }));
      setVehicles(vList);

      // Process drivers (เรียงตามลำดับ SortOrder ที่ตั้งค่าไว้)
      const dData = resDrivers.data?.data || [];
      const dList = dData
        .slice()
        .sort((a, b) => {
          const ordA = a.sortOrder && a.sortOrder > 0 ? a.sortOrder : 999999;
          const ordB = b.sortOrder && b.sortOrder > 0 ? b.sortOrder : 999999;
          if (ordA !== ordB) return ordA - ordB;
          return (a.fullName || a.userName || "").localeCompare(b.fullName || b.userName || "");
        })
        .map(d => {
          const prefix = d.sortOrder && d.sortOrder > 0 ? `${d.sortOrder}. ` : "";
          return {
            value: d.userName,
            label: `${prefix}${d.fullName || d.userName}`,
            userName: d.userName,
            fullName: `${prefix}${d.fullName || d.userName}`,
            sortOrder: d.sortOrder || 0
          };
        });
      setDrivers(dList);

      // Process assigned turns (เฉพาะรายการที่จัดรถแล้วเท่านั้น)
      const turns = resTurns.data?.data || [];
      setRawTurns(turns);
    } catch (err) {
      console.error("Error loading vehicle assignment data:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Map raw assigned turns into Calendar events with full details
  const allEvents = useMemo(() => {
    return rawTurns.map(item => {
      const requests = item.vehicleAssignmentRequest || [];
      const oneWay =
        isOneWayBooking(item) ||
        (requests.length > 0 &&
          requests.every(req => isOneWayBooking(req.vehicleBooking)));

      const matchedVeh = vehicles.find(v => v.value === item.vehicleKey);
      const matchedDriver = drivers.find(d => d.value === item.driverUserName);

      const vName =
        item.vehicleName ||
        (matchedVeh ? matchedVeh.name : `รถคันที่ ${item.vehicleKey}`);
      const vPlate =
        item.licensePlate || (matchedVeh ? matchedVeh.licensePlate : "");
      const dName =
        item.driverName ||
        (matchedDriver ? matchedDriver.fullName : item.driverUserName || "ยังไม่ระบุคนขับ");

      const color =
        perspective === "vehicle"
          ? getVehicleColor(item.vehicleKey)
          : getDriverColor(item.driverUserName);

      return {
        id: item.assignmentTurnID,
        title: item.title || "การเดินทาง",
        start: item.startDatetime,
        end: oneWay ? null : item.endDatetime,
        backgroundColor: color,
        borderColor: color,
        extendedProps: {
          assignmentTurnID: item.assignmentTurnID,
          vehicleKey: item.vehicleKey,
          vehicleName: vName,
          licensePlate: vPlate,
          driverUserName: item.driverUserName,
          driverName: dName,
          isOneWay: oneWay,
          color: color,
          rawItem: item
        }
      };
    });
  }, [rawTurns, vehicles, drivers, perspective]);

  // Filter events based on active perspective and selector
  const filteredEvents = useMemo(() => {
    if (perspective === "vehicle") {
      if (!selectedVehicleKey || selectedVehicleKey === 0) return allEvents;
      return allEvents.filter(
        e => e.extendedProps.vehicleKey === selectedVehicleKey
      );
    } else {
      if (!selectedDriverUser || selectedDriverUser === "") return allEvents;
      return allEvents.filter(
        e => e.extendedProps.driverUserName === selectedDriverUser
      );
    }
  }, [allEvents, perspective, selectedVehicleKey, selectedDriverUser]);

  // Date click from FullCalendar month view
  const handleDateClick = args => {
    setDefaultDate(args.dateStr);
  };

  // Event click to open details
  const handleEventClick = info => {
    const props = info.event?.extendedProps;
    if (props?.rawItem) {
      setSelectedTurn({
        ...props.rawItem,
        vehicleName: props.vehicleName,
        licensePlate: props.licensePlate,
        driverName: props.driverName,
        isOneWay: props.isOneWay
      });
      setDetailModal(true);
    }
  };

  // Day navigation for daily timeline columns
  const handleShiftDay = days => {
    setDefaultDate(prev =>
      moment(prev)
        .add(days, "days")
        .format("YYYY-MM-DD")
    );
  };

  // Render event content on Month View
  const renderMonthEventContent = eventInfo => {
    const ext = eventInfo.event.extendedProps;
    const timeText = formatVehicleEventTime(eventInfo);

    if (perspective === "vehicle") {
      return (
        <div
          style={{
            fontSize: "0.78rem",
            lineHeight: 1.25,
            padding: "2px 4px",
            backgroundColor: ext.color,
            color: "#ffffff",
            borderRadius: 3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: "pointer"
          }}
          title={`${ext.vehicleName} ${ext.licensePlate ? `(${ext.licensePlate})` : ""}: ${eventInfo.event.title} (ผู้ขับ: ${ext.driverName})`}
        >
          <strong>{ext.vehicleName}</strong> {timeText ? `[${timeText}]` : ""} {eventInfo.event.title}
        </div>
      );
    } else {
      return (
        <div
          style={{
            fontSize: "0.78rem",
            lineHeight: 1.25,
            padding: "2px 4px",
            backgroundColor: ext.color,
            color: "#ffffff",
            borderRadius: 3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            cursor: "pointer"
          }}
          title={`${ext.driverName}: ${eventInfo.event.title} (รถ: ${ext.vehicleName} ${ext.licensePlate})`}
        >
          <strong>{ext.driverName}</strong> {timeText ? `[${timeText}]` : ""} {eventInfo.event.title}
        </div>
      );
    }
  };

  // Render event content on List View
  const renderListEventContent = eventInfo => {
    const ext = eventInfo.event.extendedProps;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer" }}>
        <span
          style={{
            backgroundColor: ext.color,
            color: "#fff",
            padding: "2px 6px",
            borderRadius: 4,
            fontSize: "0.75rem",
            fontWeight: "bold"
          }}
        >
          {perspective === "vehicle" ? ext.vehicleName : ext.driverName}
        </span>
        <span style={{ fontWeight: 500, color: "#1e293b" }}>{eventInfo.event.title}</span>
        <span style={{ color: "#64748b", fontSize: "0.85rem" }}>
          {perspective === "vehicle"
            ? `(ผู้ขับ: ${ext.driverName})`
            : `(รถ: ${ext.vehicleName} ${ext.licensePlate})`}
        </span>
        {ext.isOneWay && (
          <span style={{ fontSize: "0.72rem", color: "#b45309", backgroundColor: "#fef3c7", padding: "1px 5px", borderRadius: 4 }}>
            เที่ยวเดียว
          </span>
        )}
      </div>
    );
  };

  // Determine which resource columns to render in Daily Timeline
  const activeResources = useMemo(() => {
    if (perspective === "vehicle") {
      if (selectedVehicleKey && selectedVehicleKey !== 0) {
        return vehicles.filter(v => v.value === selectedVehicleKey);
      }
      return vehicles;
    } else {
      if (selectedDriverUser && selectedDriverUser !== "") {
        return drivers.filter(d => d.value === selectedDriverUser);
      }
      return drivers;
    }
  }, [perspective, selectedVehicleKey, selectedDriverUser, vehicles, drivers]);

  return (
    <VehicleAdminGuard>
      <div>
        {/* Notice & Quick Action Banner */}
        <div
          style={{
            backgroundColor: "#eff6ff",
            border: "1.5px solid #bfdbfe",
            borderRadius: 10,
            padding: "12px 18px",
            marginBottom: 18,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: "1.4rem" }}>ℹ️</span>
            <div>
              <div style={{ color: "#1e40af", fontWeight: "bold", fontSize: "0.98rem" }}>
                ปฏิทินรถส่วนกลาง (แสดงเฉพาะรายการที่จัดรถแล้ว)
              </div>
              <div style={{ color: "#3b82f6", fontSize: "0.84rem" }}>
                * หมายเหตุ: ปฏิทินนี้ดึงและแสดงผลเฉพาะรายการคำขอใช้รถที่ได้รับการ <u>จัดรถและกำหนดคนขับแล้ว</u> เท่านั้น (ไม่รวมรายการที่ยังรอจัดรถ)
              </div>
            </div>
          </div>

          <Button
            color="primary"
            variant="contained"
            onClick={() => props.history.push("/vehicle/queuemanage")}
            style={{
              backgroundColor: "#2563eb",
              color: "#ffffff",
              fontWeight: "bold",
              borderRadius: 6,
              padding: "8px 16px",
              boxShadow: "0 2px 6px rgba(37,99,235,0.3)"
            }}
          >
            🚗 ไปที่หน้าจัดรถ
          </Button>
        </div>

        <Grid>
          <Col xs={12} sm={12} md={12}>
            <Card>
              <CardHeader color="primary">
                <Grid container alignItems="center" justify="space-between" spacing={1}>
                  <Col xs={12} sm={4} md={3}>
                    <h4 className={classes.cardTitleWhite}>ปฏิทินการใช้รถส่วนกลาง</h4>
                  </Col>

                  {/* Toggle 2 มุมมอง (มุมมองรถ vs มุมมองคนขับ) */}
                  <Col xs={12} sm={8} md={5}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, backgroundColor: "rgba(255,255,255,0.18)", padding: "4px 8px", borderRadius: 8 }}>
                      <span style={{ color: "#ffffff", fontSize: "0.86rem", fontWeight: "bold", marginRight: 4 }}>
                        มุมมอง:
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setPerspective("vehicle");
                          setSelectedDriverUser("");
                        }}
                        style={{
                          flex: 1,
                          padding: "6px 12px",
                          borderRadius: 6,
                          border: "none",
                          cursor: "pointer",
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                          backgroundColor: perspective === "vehicle" ? "#ffffff" : "transparent",
                          color: perspective === "vehicle" ? "#1e40af" : "#ffffff",
                          boxShadow: perspective === "vehicle" ? "0 1px 4px rgba(0,0,0,0.15)" : "none",
                          transition: "all 0.2s"
                        }}
                      >
                        🚗 มุมมองรถ
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPerspective("driver");
                          setSelectedVehicleKey(0);
                        }}
                        style={{
                          flex: 1,
                          padding: "6px 12px",
                          borderRadius: 6,
                          border: "none",
                          cursor: "pointer",
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                          backgroundColor: perspective === "driver" ? "#ffffff" : "transparent",
                          color: perspective === "driver" ? "#1e40af" : "#ffffff",
                          boxShadow: perspective === "driver" ? "0 1px 4px rgba(0,0,0,0.15)" : "none",
                          transition: "all 0.2s"
                        }}
                      >
                        👨‍✈️ มุมมองคนขับ
                      </button>
                    </div>
                  </Col>

                  {/* Switch ระหว่างแสดงปฏิทิน vs แสดงรายการ */}
                  <Col xs={12} sm={12} md={4} style={{ textAlign: "right" }}>
                    <div style={{ display: "inline-flex", alignItems: "center" }}>
                      <TSCheckBox
                        checked={isShowCalendar}
                        onChange={() => setIsShowCalendar(!isShowCalendar)}
                      />
                      <span style={{ color: "white", marginLeft: 6, fontWeight: 500 }}>
                        {isShowCalendar ? "แสดงปฏิทินรายวัน/เดือน" : "แสดงรายการสรุป"}
                      </span>
                    </div>
                  </Col>
                </Grid>
              </CardHeader>

              <CardBody>
                {/* Secondary Filter Toolbar */}
                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 8,
                    padding: "10px 16px",
                    marginBottom: 16,
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 12
                  }}
                >
                  <div style={{ minWidth: 260, flex: 1 }}>
                    {perspective === "vehicle" ? (
                      <TSSelect2
                        label="กรองเฉพาะรถคันที่ต้องการ:"
                        options={[{ value: 0, label: "รถทุกคัน (ทั้งหมด)" }, ...vehicles]}
                        value={selectedVehicleKey}
                        onChange={e => setSelectedVehicleKey(e ? e.value : 0)}
                      />
                    ) : (
                      <TSSelect2
                        label="กรองเฉพาะคนขับที่ต้องการ:"
                        options={[{ value: "", label: "คนขับทุกคน (ทั้งหมด)" }, ...drivers]}
                        value={selectedDriverUser}
                        onChange={e => setSelectedDriverUser(e ? e.value : "")}
                      />
                    )}
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
                    <span style={{ fontSize: "0.85rem", color: "#64748b" }}>วันที่เลือกดูตาราง:</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleShiftDay(-1)}
                      title="วันก่อนหน้า"
                    >
                      ◀
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setDefaultDate(moment().format("YYYY-MM-DD"))}
                    >
                      วันนี้
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleShiftDay(1)}
                      title="วันถัดไป"
                    >
                      ▶
                    </button>
                    <span style={{ fontWeight: "bold", color: "#0f172a", fontSize: "0.92rem", marginLeft: 4 }}>
                      {moment(defaultDate).format("D MMMM YYYY")}
                    </span>
                  </div>
                </div>

                {/* Main Content Area */}
                {!isShowCalendar ? (
                  /* 1. LIST VIEW */
                  <Fullcalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
                    initialView="listMonth"
                    headerToolbar={{
                      start: "listDay,listWeek,listMonth",
                      center: "title",
                      end: "today prev,next"
                    }}
                    views={{
                      listDay: { buttonText: "วัน" },
                      listWeek: { buttonText: "สัปดาห์" },
                      listMonth: { buttonText: "เดือน" }
                    }}
                    height="85vh"
                    events={filteredEvents}
                    eventClick={handleEventClick}
                    locale="th"
                    eventContent={renderListEventContent}
                  />
                ) : (
                  /* 2. CALENDAR VIEW (Top: Month Grid | Bottom: Daily Columns by Resource) */
                  <Grid>
                    {/* Top: Monthly Overview Calendar */}
                    <Col xs={12} sm={12} md={12} lg={12} style={{ marginBottom: 16 }}>
                      <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 12, backgroundColor: "#ffffff" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid #e2e8f0", flexWrap: "wrap", gap: 6 }}>
                          <span style={{ fontSize: "0.95rem", fontWeight: "bold", color: "#1e293b" }}>
                            📅 ปฏิทินภาพรวม ({perspective === "vehicle" ? "แยกตามรถ" : "แยกตามคนขับ"})
                          </span>
                          <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                            คลิกที่วันที่ในปฏิทินเพื่อเลือกดูตารางการใช้รถ/คนขับรายวันด้านล่าง
                          </span>
                        </div>
                        <Fullcalendar
                          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                          initialView="dayGridMonth"
                          headerToolbar={{
                            start: "title",
                            center: "",
                            end: "today prev,next"
                          }}
                          height="auto"
                          dateClick={handleDateClick}
                          events={filteredEvents}
                          eventClick={handleEventClick}
                          locale="th"
                          eventContent={renderMonthEventContent}
                        />
                      </div>
                    </Col>

                    {/* Bottom: Daily Columns for Selected Date */}
                    <Col xs={12} sm={12} md={12} lg={12}>
                      <div
                        style={{
                          border: "1px solid #e2e8f0",
                          borderRadius: 8,
                          padding: 12,
                          backgroundColor: "#ffffff",
                          minHeight: "50vh"
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 8,
                            paddingBottom: 6,
                            borderBottom: "1px solid #e2e8f0"
                          }}
                        >
                          <span style={{ fontWeight: "bold", fontSize: "0.95rem", color: "#0f172a" }}>
                            ตารางรายวันประจำวันที่ {moment(defaultDate).format("D MMMM YYYY")} ({activeResources.length} คอลัมน์)
                          </span>
                          <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                            คลิกที่รายการในตารางเพื่อดูรายละเอียดการจัดรถ
                          </span>
                        </div>

                        {activeResources.length === 0 ? (
                          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
                            ไม่พบข้อมูลทรัพยากร ({perspective === "vehicle" ? "รถ" : "คนขับ"}) สำหรับแสดงผล
                          </div>
                        ) : (
                          <div
                            style={{
                              display: "flex",
                              gap: 10,
                              overflowX: "auto",
                              paddingBottom: 8
                            }}
                          >
                            {activeResources.map(res => {
                              const resEvents =
                                perspective === "vehicle"
                                  ? allEvents.filter(e => e.extendedProps.vehicleKey === res.value)
                                  : allEvents.filter(e => e.extendedProps.driverUserName === res.value);

                              const headerLabel =
                                perspective === "vehicle"
                                  ? `${res.name} ${res.licensePlate ? `(${res.licensePlate})` : ""}`
                                  : res.fullName || res.userName;

                              const headerColor =
                                perspective === "vehicle"
                                  ? getVehicleColor(res.value)
                                  : getDriverColor(res.value);

                              return (
                                <div
                                  key={`col-${perspective}-${res.value}`}
                                  style={{
                                    flex: activeResources.length === 1 ? 1 : "0 0 240px",
                                    minWidth: 220,
                                    maxWidth: activeResources.length === 1 ? "100%" : 320,
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 6,
                                    overflow: "hidden"
                                  }}
                                >
                                  <Calendar
                                    defaultDate={defaultDate}
                                    customHeader={headerLabel}
                                    id={res.value}
                                    color={headerColor}
                                    headerColor={headerColor}
                                    eventBackgroundColor={headerColor}
                                    minTime="06:00:00"
                                    maxTime="20:00:00"
                                    events={resEvents}
                                    disabledCreate={true}
                                    onEventClick={handleEventClick}
                                    eventContent={info => {
                                      const ext = info.event.extendedProps;
                                      return (
                                        <div
                                          style={{
                                            padding: "2px 4px",
                                            backgroundColor: headerColor,
                                            color: "#ffffff",
                                            borderRadius: 4,
                                            fontSize: "0.78rem",
                                            cursor: "pointer"
                                          }}
                                        >
                                          <div style={{ fontWeight: "bold" }}>{info.event.title}</div>
                                          <div style={{ fontSize: "0.72rem", opacity: 0.9 }}>
                                            {perspective === "vehicle"
                                              ? `ผู้ขับ: ${ext.driverName}`
                                              : `รถ: ${ext.vehicleName} ${ext.licensePlate}`}
                                          </div>
                                        </div>
                                      );
                                    }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Grid>
                )}
              </CardBody>
            </Card>
          </Col>
        </Grid>

        {/* Trip Detail Dialog */}
        <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="md">
          <ModalHeader toggle={() => setDetailModal(false)}>
            รายละเอียดการจัดรถ
          </ModalHeader>
          <ModalBody>
            {selectedTurn && (
              <div style={{ fontSize: "0.92rem", lineHeight: 1.6 }}>
                <div style={{ backgroundColor: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: 12, marginBottom: 14 }}>
                  <div style={{ color: "#166534", fontWeight: "bold", fontSize: "1.05rem" }}>
                    📍 {selectedTurn.title || "ไม่ระบุจุดหมาย"}
                  </div>
                  {selectedTurn.isOneWay && (
                    <span style={{ fontSize: "0.75rem", color: "#b45309", backgroundColor: "#fef3c7", padding: "2px 6px", borderRadius: 4, fontWeight: "bold", marginTop: 4, display: "inline-block" }}>
                      เที่ยวเดียว
                    </span>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: "8px 12px", marginBottom: 12 }}>
                  <span style={{ color: "#64748b", fontWeight: 500 }}>วันเวลาเดินทาง:</span>
                  <span style={{ color: "#0f172a", fontWeight: "bold" }}>
                    {moment(selectedTurn.startDatetime).format("D MMM YYYY HH:mm")}
                    {!selectedTurn.isOneWay && selectedTurn.endDatetime
                      ? ` ถึง ${moment(selectedTurn.endDatetime).format("D MMM YYYY HH:mm")}`
                      : " (เที่ยวเดียว)"}
                  </span>

                  <span style={{ color: "#64748b", fontWeight: 500 }}>ยานพาหนะ:</span>
                  <span style={{ color: "#0f172a" }}>
                    🚗 <strong>{selectedTurn.vehicleName}</strong> {selectedTurn.licensePlate ? `(ทะเบียน: ${selectedTurn.licensePlate})` : ""}
                  </span>

                  <span style={{ color: "#64748b", fontWeight: 500 }}>พนักงานขับรถ:</span>
                  <span style={{ color: "#0f172a" }}>
                    👨‍✈️ <strong>{selectedTurn.driverName}</strong>
                  </span>
                </div>

                {selectedTurn.vehicleAssignmentRequest && selectedTurn.vehicleAssignmentRequest.length > 0 && (
                  <div style={{ marginTop: 12, borderTop: "1px solid #e2e8f0", paddingTop: 10 }}>
                    <div style={{ fontWeight: "bold", color: "#334155", marginBottom: 6 }}>
                      คำขอที่รวมในเที่ยวนี้ ({selectedTurn.vehicleAssignmentRequest.length} คำขอ):
                    </div>
                    {selectedTurn.vehicleAssignmentRequest.map((req, idx) => {
                      const vb = req.vehicleBooking || {};
                      return (
                        <div key={idx} style={{ backgroundColor: "#f8fafc", padding: "8px 10px", borderRadius: 6, marginBottom: 6, border: "1px solid #e2e8f0" }}>
                          <div><strong>ปลายทาง:</strong> {vb.endPlace || "-"}</div>
                          <div><strong>วัตถุประสงค์:</strong> {vb.vbObjective || "-"}</div>
                          <div><strong>ผู้ขอ:</strong> {vb.creatorCode || "-"}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={() => {
                setDetailModal(false);
                props.history.push("/vehicle/queuemanage");
              }}
              style={{ backgroundColor: "#2563eb", fontWeight: "bold" }}
            >
              ไปที่หน้าจัดรถ
            </Button>
            <Button color="secondary" onClick={() => setDetailModal(false)}>
              ปิด
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </VehicleAdminGuard>
  );
};

export default Home;
